import { availabilityPresentation } from "./availability.js";
import {
  DEFAULT_QUERY,
  filterAndSort,
  parseQuery,
  serializeQuery,
} from "./query-state.js";

const FLAVOUR_PROFILE_COLOURS = new Map([
  ["light delicate", "#90CEDD"],
  ["fragrant floral", "#E2848E"],
  ["young spritely", "#C294BF"],
  ["sweet zesty", "#DC7130"],
  ["sweet fruity mellow", "#9B61A2"],
  ["ripe fruits honey", "#7E2975"],
  ["juicy oak vanilla", "#6AA6CB"],
  ["toasted oak vanilla", "#E9A552"],
  ["spicy dry", "#EEAE69"],
  ["spicy sweet", "#6A3A81"],
  ["old dignified", "#AD4441"],
  ["dried fruits spices", "#C22F25"],
  ["deep rich dried fruits", "#D98246"],
  ["coastal maritime", "#2C6491"],
  ["oily coastal", "#3B6D97"],
  ["smoky fruity", "#8EBC5B"],
  ["lightly peated", "#B8D698"],
  ["peated", "#7FB35C"],
  ["heavily peated", "#447644"],
  ["bold peaty", "#327541"],
]);

function normalizeFlavourProfile(value) {
  return String(value ?? "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\band\b/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

function flavourProfilePresentation(value) {
  const colour = FLAVOUR_PROFILE_COLOURS.get(normalizeFlavourProfile(value));
  if (!colour) return null;
  const [red, green, blue] = [1, 3, 5]
    .map((offset) => Number.parseInt(colour.slice(offset, offset + 2), 16));
  const luminance = (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255;
  return { colour, ink: luminance > 0.62 ? "#211b18" : "#ffffff" };
}

function applyFlavourProfile(container, value) {
  const presentation = flavourProfilePresentation(value);
  if (!presentation) return;
  container.classList.add("has-flavour-profile");
  container.style.setProperty("--flavour-profile-colour", presentation.colour);
  container.style.setProperty("--flavour-profile-ink", presentation.ink);
}

export const BOTTLE_FIELDS = Object.freeze([
  ["Bottle name", "name"],
  ["Cask No.", "code"],
  ["Rating", "rating"],
  ["Distillery", "distillery"],
  ["Price", "price"],
  ["Flavour profile", "flavourProfile"],
  ["Region", "region"],
  ["ABV", "abv"],
  ["Age", "age"],
  ["Cask type", "caskType"],
  ["TWB Review", "reviewScore"],
]);

function externalLink(document, { href, text, className = "" }) {
  if (!href) return document.createTextNode(text || "—");
  const link = document.createElement("a");
  link.href = href;
  link.textContent = text || "—";
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  if (className) link.className = className;
  return link;
}

function bottleName(document, record) {
  return externalLink(document, {
    href: record.smwsUrl,
    text: record.name || record.code,
    className: "name-link",
  });
}

function ratingSlot(document, code) {
  const slot = document.createElement("div");
  slot.className = "account-rating";
  slot.dataset.ratingBottle = code;
  const summary = document.createElement("span");
  summary.className = "rating-summary";
  summary.textContent = "…";
  summary.setAttribute("aria-label", "Loading rating");
  const action = document.createElement("button");
  action.type = "button";
  action.className = "rating-action";
  action.dataset.rateBottle = code;
  action.textContent = "Rate";
  slot.append(summary, action);
  return slot;
}

function availabilitySlot(document, code) {
  const slot = document.createElement("span");
  slot.className = "bottle-availability";
  slot.dataset.availabilityBottle = code;
  slot.hidden = true;
  return slot;
}

function collectionSlot(document, code, kind) {
  const slot = document.createElement("div");
  slot.className = "collection-row-action";
  slot.dataset.collectionBottle = code;
  slot.dataset.collectionKind = kind;
  return slot;
}

function appendBottleField(container, { document, record, key, options }) {
  if (key === "name") {
    container.append(
      bottleName(document, record),
      availabilitySlot(document, record.code),
      collectionSlot(
        document,
        record.code,
        options.collectionKind ?? options.section ?? "browse",
      ),
    );
    return;
  }
  if (key === "rating") {
    container.append(ratingSlot(document, record.code));
    return;
  }
  if (key === "flavourProfile") {
    const badge = document.createElement("span");
    badge.className = "flavour-profile-badge";
    badge.textContent = record.flavourProfile ?? "";
    badge.title = record.flavourProfile ?? "";
    container.append(badge);
    return;
  }
  if (key === "reviewScore") {
    container.append(externalLink(document, {
      href: record.reviewUrl,
      text: record.reviewScore || "—",
    }));
    return;
  }
  container.textContent = record[key] ?? "";
}

export function createBottleRow(document, record, options = {}) {
  const row = document.createElement("tr");
  row.dataset.bottleCode = record.code;
  applyFlavourProfile(row, record.flavourProfile);
  for (const [label, key] of BOTTLE_FIELDS) {
    const cell = document.createElement("td");
    cell.dataset.label = label;
    cell.className = `col-${key.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}`;
    appendBottleField(cell, { document, record, key, options });
    row.append(cell);
  }
  return row;
}

export function createBottleCard(document, record, options = {}) {
  const card = document.createElement("article");
  card.className = "bottle-card";
  card.dataset.bottleCode = record.code;
  card.dataset.collectionKind = options.collectionKind ?? options.section ?? "browse";
  applyFlavourProfile(card, record.flavourProfile);

  for (const [label, key] of BOTTLE_FIELDS) {
    const field = document.createElement("div");
    field.className = `bottle-field bottle-field-${key.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}`;
    field.dataset.field = label;
    const fieldLabel = document.createElement("span");
    fieldLabel.className = "bottle-field-label";
    fieldLabel.textContent = label;
    const value = document.createElement("div");
    value.className = "bottle-field-value";
    appendBottleField(value, { document, record, key, options });
    field.append(fieldLabel, value);
    card.append(field);
  }
  return card;
}

function createTableHeader(document) {
  const header = document.createElement("thead");
  const row = document.createElement("tr");
  for (const [label, key] of BOTTLE_FIELDS) {
    const cell = document.createElement("th");
    cell.scope = "col";
    cell.textContent = label;
    cell.className = `col-${key.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}`;
    row.append(cell);
  }
  header.append(row);
  return header;
}

function renderAvailability(root, availability) {
  for (const slot of root.querySelectorAll("[data-availability-bottle]")) {
    const item = availability.get(slot.dataset.availabilityBottle);
    const presentation = availabilityPresentation(item);
    slot.hidden = false;
    slot.textContent = ` · ${presentation.label}`;
    slot.className = `bottle-availability ${presentation.className}`;
    if (item?.resolvedUrl) {
      const link = slot.parentElement?.querySelector("a.name-link");
      if (link) link.href = item.resolvedUrl;
    }
  }
}

export function createCatalogueView({
  document,
  pageSize = 60,
  onMount = () => {},
  sectionRoots = null,
}) {
  const results = document.querySelector("#catalogueResults");
  const resultsMeta = document.querySelector("#resultsMeta");
  const loadMore = document.querySelector("#loadMoreResults");
  if (!results && !sectionRoots) throw new Error("Catalogue results root was not found");
  let sections = [];
  let query = cloneQuery(DEFAULT_QUERY);
  let availability = new Map();
  let visibleCount = pageSize;

  function renderResultsMeta(text) {
    if (!resultsMeta) return;
    const visible = hasActiveFilters(query);
    resultsMeta.hidden = !visible;
    resultsMeta.textContent = visible ? text : "";
  }

  function matchedSections() {
    if (!sectionRoots && query.groupByOutturn === false) {
      return [{
        key: "all",
        title: "All Bottles",
        records: filterAndSort(
          sections.flatMap((section) => section.records),
          query,
          availability,
        ),
      }];
    }
    return sections.map((section) => ({
      ...section,
      records: filterAndSort(section.records, query, availability),
    }));
  }

  function renderSection(section, records) {
    const wrapper = document.createElement("section");
    wrapper.className = "catalogue-section";
    wrapper.dataset.sectionKey = section.key;
    const heading = document.createElement("h2");
    heading.className = "outturn-heading";
    heading.textContent = section.title;
    wrapper.append(heading);
    if (query.view === "gallery") {
      const grid = document.createElement("div");
      grid.className = "bottle-card-grid";
      for (const record of records) {
        grid.append(createBottleCard(document, record, {
          section: section.collectionKind ?? "browse",
        }));
      }
      wrapper.append(grid);
    } else {
      const tableWrapper = document.createElement("div");
      tableWrapper.className = "wrap";
      const table = document.createElement("table");
      table.className = "outturn-table catalogue-table";
      const body = document.createElement("tbody");
      for (const record of records) {
        body.append(createBottleRow(document, record, {
          section: section.collectionKind ?? "browse",
        }));
      }
      table.append(createTableHeader(document), body);
      tableWrapper.append(table);
      wrapper.append(tableWrapper);
    }
    return wrapper;
  }

  function render() {
    const matched = matchedSections();
    const total = matched.reduce((sum, section) => sum + section.records.length, 0);
    if (sectionRoots) {
      for (const section of matched) {
        const root = sectionRoots[section.key];
        if (!root) continue;
        if (!section.records.length) {
          const empty = document.createElement("p");
          empty.className = "collection-empty";
          empty.textContent = `No bottles in ${section.title}.`;
          root.replaceChildren(empty);
        } else {
          const rendered = renderSection(section, section.records);
          rendered.querySelector("h2")?.remove();
          root.replaceChildren(...rendered.childNodes);
        }
        renderAvailability(root, availability);
        onMount(root);
      }
      renderResultsMeta(`${total} ${total === 1 ? "bottle" : "bottles"}`);
      return;
    }
    let budget = Math.min(visibleCount, total);
    let shown = 0;
    const fragment = document.createDocumentFragment();
    for (const section of matched) {
      if (budget <= 0) break;
      const visible = section.records.slice(0, budget);
      if (!visible.length) continue;
      shown += visible.length;
      budget -= visible.length;
      fragment.append(renderSection(section, visible));
    }
    if (!total) {
      const empty = document.createElement("p");
      empty.className = "catalogue-empty";
      empty.textContent = "No bottles match these filters.";
      fragment.append(empty);
    }
    results.replaceChildren(fragment);
    renderAvailability(results, availability);
    onMount(results);
    renderResultsMeta(shown === total
      ? `${total} ${total === 1 ? "bottle" : "bottles"}`
      : `Showing ${shown} of ${total} bottles`);
    if (loadMore) {
      const next = Math.min(pageSize, Math.max(0, total - shown));
      loadMore.hidden = next === 0;
      loadMore.textContent = next ? `Show next ${next}` : "";
    }
  }

  loadMore?.addEventListener("click", () => {
    visibleCount += pageSize;
    render();
  });

  return {
    setSections(next) {
      sections = next.map((section) => ({ ...section, records: [...section.records] }));
      visibleCount = pageSize;
      render();
    },
    setQuery(next) {
      query = cloneQuery(next);
      visibleCount = pageSize;
      render();
    },
    setAvailability(next) {
      availability = next ?? new Map();
      render();
    },
    showNextBatch() {
      visibleCount += pageSize;
      render();
    },
    render,
  };
}

const NUMBER_CONTROLS = ["priceMin", "priceMax", "ageMin", "ageMax", "scoreMin", "scoreMax"];

function cloneQuery(query) {
  return { ...query, profiles: [...query.profiles], regions: [...query.regions] };
}

function hasActiveFilters(query) {
  return Boolean(
    query.q
    || query.profiles.length
    || query.regions.length
    || NUMBER_CONTROLS.some((id) => query[id] != null)
    || query.availableOnly,
  );
}

function sortParts(sort) {
  if (!sort || sort === "catalogue") return { field: "default", direction: "asc" };
  const separator = sort.lastIndexOf("-");
  return { field: sort.slice(0, separator), direction: sort.slice(separator + 1) };
}

export function bindQueryControls({
  document,
  history = document.defaultView?.history,
  location = document.defaultView?.location,
  onChange = () => {},
}) {
  let query = parseQuery(location?.search ?? "");
  const listeners = [];
  const listen = (target, type, handler) => {
    target?.addEventListener(type, handler);
    if (target) listeners.push(() => target.removeEventListener(type, handler));
  };

  let chips = document.querySelector("#activeFilters");
  if (!chips) {
    chips = document.createElement("div");
    chips.id = "activeFilters";
    chips.className = "active-filters";
    chips.setAttribute("aria-label", "Active filters");
    document.querySelector(".toolbar-wrap")?.after(chips);
  }

  function setNumberControl(id, value) {
    const input = document.querySelector(`#${id}`);
    if (input) input.value = value == null ? "" : String(value);
  }

  function renderControls() {
    const search = document.querySelector("#searchInput");
    if (search) search.value = query.q;
    for (const input of document.querySelectorAll('input[name="profile"]')) {
      input.checked = query.profiles.includes(input.value);
    }
    for (const input of document.querySelectorAll('input[name="region"]')) {
      input.checked = query.regions.includes(input.value);
    }
    for (const id of NUMBER_CONTROLS) setNumberControl(id, query[id]);
    const available = document.querySelector("#availableOnly");
    if (available) available.checked = query.availableOnly;
    const groupByOutturn = document.querySelector("#groupByOutturn");
    if (groupByOutturn) groupByOutturn.checked = query.groupByOutturn !== false;
    const { field, direction } = sortParts(query.sort);
    const sortField = document.querySelector("#sortField");
    if (sortField) sortField.value = field;
    for (const button of document.querySelectorAll("[data-dir]")) {
      const active = button.dataset.dir === direction;
      button.classList.toggle("active", active);
      button.setAttribute("aria-pressed", String(active));
    }
    for (const button of document.querySelectorAll("[data-view]")) {
      const active = button.dataset.view === query.view;
      button.setAttribute("aria-checked", String(active));
      button.classList.toggle("active", active);
    }
    document.body.classList.toggle("view-gallery", query.view === "gallery");
    document.body.classList.toggle("view-list", query.view !== "gallery");
    const clear = document.querySelector("#clearFiltersTop");
    if (clear) clear.disabled = !hasActiveFilters(query);
    renderChips();
  }

  function navigate(next, mode = "push") {
    query = cloneQuery(next);
    const url = `${location?.pathname ?? "/"}${serializeQuery(query)}${location?.hash ?? ""}`;
    history?.[mode === "replace" ? "replaceState" : "pushState"]?.({}, "", url);
    renderControls();
    onChange(cloneQuery(query));
  }

  function chip(key, label, remove) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "filter-chip";
    button.dataset.filterChip = key;
    button.setAttribute("aria-label", `Remove ${label} filter`);
    button.textContent = `${label} ×`;
    button.addEventListener("click", () => navigate(remove(cloneQuery(query))));
    chips.append(button);
  }

  function renderChips() {
    chips.replaceChildren();
    if (query.q) chip("q", `Search: ${query.q}`, (next) => ({ ...next, q: "" }));
    for (const profile of query.profiles) {
      chip(`profile:${profile}`, profile, (next) => ({
        ...next, profiles: next.profiles.filter((value) => value !== profile),
      }));
    }
    for (const region of query.regions) {
      chip(`region:${region}`, region, (next) => ({
        ...next, regions: next.regions.filter((value) => value !== region),
      }));
    }
    for (const [minimum, maximum, label] of [
      ["priceMin", "priceMax", "Price"],
      ["ageMin", "ageMax", "Age"],
      ["scoreMin", "scoreMax", "Score"],
    ]) {
      if (query[minimum] == null && query[maximum] == null) continue;
      const range = `${query[minimum] ?? "any"}–${query[maximum] ?? "any"}`;
      chip(`${minimum}:${maximum}`, `${label}: ${range}`, (next) => ({
        ...next, [minimum]: null, [maximum]: null,
      }));
    }
    if (query.availableOnly) {
      chip("available", "Available now", (next) => ({ ...next, availableOnly: false }));
    }
    chips.hidden = chips.childElementCount === 0;
  }

  const search = document.querySelector("#searchInput");
  listen(search, "input", () => navigate({ ...query, q: search.value }, "replace"));
  listen(document.querySelector("#clearSearch"), "click", () => navigate({ ...query, q: "" }));
  const filterPanel = document.querySelector("#filterPanel");
  listen(filterPanel, "change", () => {
    const next = cloneQuery(query);
    next.profiles = [...document.querySelectorAll('input[name="profile"]:checked')]
      .map((input) => input.value);
    next.regions = [...document.querySelectorAll('input[name="region"]:checked')]
      .map((input) => input.value);
    for (const id of NUMBER_CONTROLS) {
      const value = document.querySelector(`#${id}`)?.value;
      next[id] = value === "" || value == null ? null : Number(value);
    }
    next.availableOnly = Boolean(document.querySelector("#availableOnly")?.checked);
    navigate(next);
  });
  listen(document.querySelector("#availableOnly"), "change", (event) => {
    navigate({ ...query, availableOnly: event.currentTarget.checked });
  });
  listen(document.querySelector("#groupByOutturn"), "change", (event) => {
    navigate({ ...query, groupByOutturn: event.currentTarget.checked });
  });
  const sortField = document.querySelector("#sortField");
  listen(sortField, "change", () => {
    const direction = sortParts(query.sort).direction;
    navigate({ ...query, sort: sortField.value === "default" ? "catalogue" : `${sortField.value}-${direction}` });
  });
  for (const button of document.querySelectorAll("[data-dir]")) {
    listen(button, "click", () => {
      const field = document.querySelector("#sortField")?.value ?? "default";
      navigate({ ...query, sort: field === "default" ? "catalogue" : `${field}-${button.dataset.dir}` });
    });
  }
  for (const button of document.querySelectorAll("[data-view]")) {
    listen(button, "click", () => navigate({ ...query, view: button.dataset.view }));
  }
  listen(document.querySelector("#clearFiltersTop"), "click", () => navigate({
    ...cloneQuery(DEFAULT_QUERY),
    groupByOutturn: query.groupByOutturn,
    sort: query.sort,
    view: query.view,
  }));
  const view = document.defaultView;
  listen(view, "popstate", () => {
    query = parseQuery(location?.search ?? "");
    renderControls();
    onChange(cloneQuery(query));
  });

  renderControls();
  onChange(cloneQuery(query));
  return {
    getQuery: () => cloneQuery(query),
    setQuery: (next, { replace = false } = {}) => navigate(next, replace ? "replace" : "push"),
    destroy: () => listeners.splice(0).forEach((remove) => remove()),
  };
}
